import { Helmet } from 'react-helmet-async'
import { useLocation } from 'react-router-dom'

import { faAlarmClock } from '@fortawesome/pro-light-svg-icons'

import {
  Box, Button, Columns, DashboardModule, LoadingModule,
} from '@domain-group/suit-theme'

import useAgencySelector from '@hooks/useAgencySelector'
import useCustomDeadlines from '@hooks/useCustomDeadlines'
import useCustomDeadline, { generateUrls } from '@hooks/useCustomDeadline'

import CustomDeadlineListItem from '@components/CustomDeadlineListItem'
import ListBlankState from '@components/ListBlankState'
import NotificationBannerLocation from '@components/NotificationBannerLocation'
import NotificationModuleLocation from '@components/NotificationModuleLocation'
import PageHeader from '@components/PageHeader'

interface PageContentProps {
  pageFilters: {},
}

const PageContent = (props: PageContentProps) => {
  const { pageFilters } = props

  // useLocation to refresh the window.location. If not used location won't reload if URL changes
  useLocation()

  const { selectedAgencyId } = useAgencySelector()

  const customDeadlinesPayload = useCustomDeadlines({
    filters: {
      owner_id: selectedAgencyId,
      owner_type: 'Agency',
      ...pageFilters,
    },
    performHttpRequests: true,
    requestOptions: {},
  })

  const {
    callbacks: {
      loadMore,
    },
    canLoadMore,
    filteredCustomDeadlines,
    filteredCustomDeadlinesCount,
    hasFilteredCustomDeadlines,
    loading,
  } = customDeadlinesPayload

  const useCustomDeadlinePayload = useCustomDeadline({ owner_id: selectedAgencyId, owner_type: 'Agency' })
  const {
    callbacks:
     { createOrEditCustomDeadline },
  } = useCustomDeadlinePayload

  const { customDeadlinesIndexUrl } = generateUrls({}, { prefixRoute: `agencies/${selectedAgencyId}/manage` })

  return (
    <>
      <Helmet>
        <title>Custom Deadlines | Realhub</title>
      </Helmet>

      <PageHeader
        actionContent={(
          <Button buttonStyle="primaryCreate" onClick={() => createOrEditCustomDeadline()} size="medium">
            Create
          </Button>
        )}
        activeTabBarItemKey="custom_deadlines"
        nestedNavigation
        tabBarItems={[
          {
            href: customDeadlinesIndexUrl,
            icon: faAlarmClock,
            key: 'custom_deadlines',
            title: 'Custom Deadlines',
          },
        ]}
        title="Custom Deadlines"
      />

      <Box paddingX="large" paddingTop={[112, 105]}>
        <Columns boxProps={{ marginTop: 'large' }} flexDirection={['column', 'column', 'row']}>
          <Columns.Main>
            <NotificationBannerLocation locationKey="agency.agencyManager.customDeadlines.banner" />

            <LoadingModule loading={loading} times={3} />

            {!loading && !hasFilteredCustomDeadlines && <ListBlankState />}

            {!loading && hasFilteredCustomDeadlines && (
              <DashboardModule contentBoxProps={{ flexDirection: 'column' }} title="Custom Deadlines">
                <Box flexDirection="column">
                  {filteredCustomDeadlines.map(customDeadline => (
                    <CustomDeadlineListItem
                      customDeadline={customDeadline}
                      isClickable
                      key={customDeadline.id}
                    />
                  ))}
                </Box>

                <DashboardModule.LoadMoreFooter
                  callbacks={{ loadMore }}
                  canLoadMore={canLoadMore}
                  entityCount={filteredCustomDeadlinesCount}
                  loading={loading}
                />
              </DashboardModule>
            )}
          </Columns.Main>

          <Columns.Sidebar>
            <NotificationModuleLocation locationKey="agency.agencyManager.customDeadlines.sidebar" />
          </Columns.Sidebar>
        </Columns>
      </Box>
    </>
  )
}

export default PageContent
